// @flow
/* global VERSION, BUILD_NUMBER */

import 'isomorphic-fetch'
import { Config } from './config'
import { getMaskAsUserId } from './maskAsUser'
import { logger } from './logger'
import { logout } from '../ducks/user/creators'
import { NetworkResponseError } from './errors'
import uuid from 'uuid/v4'

import type { Dispatch } from 'redux'

/** @module util/api */

/**
 * Create an authorization header object
 * @param {string} bearerToken - Authorization bearer token
 * @returns {object} An Authorization header object
 */
export function createAuthHeaderObject(bearerToken?: string = '') {
  return {
    Authorization: `Bearer ${bearerToken}`
  }
}

/**
 * Create Client Version Header Object
 * @return {null|object} - `Client-Version` header
 */
const createClientVersionHeaderObject = () => {
  // $FlowFixMe
  if (typeof VERSION !== 'undefined' && typeof BUILD_NUMBER !== 'undefined') {
    return {
      'Client-Version': VERSION + '-' + BUILD_NUMBER
    }
  }
}

/**
 * Create `requestas` header when the current user is being masked as another user
 * @return {object} - Object with `requestas` property (if appropriate)
 */
export const createRequestAsHeaderObject = (): ?{ requestas: string } => {
  const requestas = getMaskAsUserId()
  if (requestas) {
    return {
      requestas
    }
  }
}

/**
 * Create an object with default headers
 * @param {string} bearerToken - Authorization bearer token
 * @returns {object} Default header object
 */
export const createDefaultHeaders = (bearerToken: ?string) => ({
  ...(bearerToken ? createAuthHeaderObject(bearerToken) : {}),
  ...createClientVersionHeaderObject(),
  ...createRequestAsHeaderObject(),
  RequestId: uuid()
})

/**
 * Create an object with default headers without RequestAs Header Object
 * @param {string} bearerToken - Authorization bearer token
 * @returns {object} header object without requestas header object
 */
export const createHeadersWithoutRequestAsObject = (bearerToken: ?string) => ({
  ...(bearerToken ? createAuthHeaderObject(bearerToken) : {}),
  ...createClientVersionHeaderObject(),
  RequestId: uuid()
})

/**
 * Dispatch logout action if response.status === 403
 * @param {object} response - Fetch response
 */
const handle403Error = (response: Response): void => {
  // Ignore 403s if we're masking as another user (they happen frequently)
  if (response.status === 403 && !getMaskAsUserId()) {
    import(/* webpackChunkName: "store" */ '../store/store').then(mod => {
      logger.info('LOGGING_OUT_DUE_TO_BAD_AUTH', { response })
      mod.Store.dispatch(logout())
    })
  }
}

/**
 * Creates, logs, and returns rejected Promise if `response` status is not `ok`, otherwise returns response
 * @param {string} url - The url the response is from
 * @param {object} response - Fetch response
 * @param {object} originalFetchOptions - Fetch options for the call that failed
 * @returns {promise} The passed in `response`, or a rejected Promise if error found
 */
export function checkResponseForErrors(
  url: string,
  response: Response,
  originalFetchOptions: {}
): Promise<any> {
  if (!response.ok) {
    // if body uses modern Response API and thus has json() meethod, convert to JSON so we have access to error message
    const hasJsonMethod = response.json instanceof Function
    if (hasJsonMethod) {
      return response.json().then(resolvedBody => {
        const message = resolvedBody.message || ''
        const networkResponseError = new NetworkResponseError(
          url,
          resolvedBody,
          response.status,
          message.slice(message.lastIndexOf(':') + 1), // remove typical backend message prefix; strings without ':' will return unaltered
          originalFetchOptions
        )

        // Log error
        logger.error(networkResponseError)

        // Make sure it's not an auth error
        handle403Error(response)

        // Return error
        return Promise.reject(networkResponseError)
      })
    }

    const networkResponseError = new NetworkResponseError(
      url,
      {},
      response.status,
      '',
      originalFetchOptions
    )

    // Log error
    logger.error(networkResponseError)

    // Make sure it's not an auth error
    handle403Error(response)

    // Return error
    return Promise.reject(networkResponseError)
  }

  return Promise.resolve(response)
}

/**
 * Convert JSON object to URL encoded string
 * @param {Object} json - Object to be encoded
 * @returns {string} The URL encoded string
 */
export function URLEncodeJSON(json: {} = {}) {
  let retStr = ''

  Object.keys(json).forEach(key => {
    retStr += `${encodeURIComponent(key)}=${encodeURIComponent(json[key])}&`
  })

  return retStr
}

/**
 * Generic API class; currently a wrapper around `fetch`
 * @class
 * @property {string} url - Protocol, any subdomains, and domain name of api url
 * @property {string} clientID - Client id
 * @property {string} clientSecret - Client secret
 * @property {string} authUrl - URL for authorization service
 */
export class API {
  url: string
  clientID: string
  clientSecret: string
  authUrl: string

  /** @constructor */
  constructor() {
    this.url = Config.API_URL
    this.clientID = Config.CLIENT_ID
    this.clientSecret = Config.CLIENT_SECRET
    this.authUrl = Config.AUTH_SERVICE_URL
  }

  /**
   * Call resource
   * @param {string} resource - Resource path, excluding leading forward slash
   * @param {Object} fetchOptions - Options to pass to `fetch` call
   * @returns {Promise} - Fetch request
   */
  call(
    resource: string = '/',
    fetchOptions: { body?: string, headers?: { RequestId?: string } } = {}
  ): Promise<any> {
    if (!this.url) {
      throw new Error(
        'Trying to use API.call() before the config is initialized'
      )
    }

    const URL = this.url + '/' + resource

    // Track all requests
    logger.info('API_REQUEST', {
      URL,
      fetchOptions,
      body: fetchOptions.body,
      requestId:
        fetchOptions.headers && fetchOptions.headers.RequestId
          ? fetchOptions.headers.RequestId
          : 'NULL'
    })

    /**
     * Do cleanup of response
     * @param {Object} response - Response object to parse
     * @returns {Object} Parsed response
     */
    function parseResponse(response) {
      // This is a placeholder for any necessary message translation
      return response
    }

    // NOTE: We should not have a catch block here as the calling functions
    // should respond to errors themselves
    // $FlowFixMe
    return fetch(URL, fetchOptions)
      .catch(err => {
        const networkErr = new NetworkResponseError(
          URL,
          null,
          null,
          'FAIL',
          fetchOptions,
          err
        )
        logger.error(networkErr)
        // $FlowFixMe
        return networkErr
      })
      .then(response => checkResponseForErrors(URL, response, fetchOptions))
      .then(response => parseResponse(response))
  }
}

export default API

// Util ///////////////////////////////////////////////////////////////////////

/**
 * apply signature string to error message
 * @param {string} message - any string
 * @param {string} signature - signature to apply if it exists
 * @returns {string} new string with signature applied
 */
const withSignature = (message: string, signature?: string) => {
  return message + (signature ? '  SIGNATURE: ' + signature : '')
}

/**
 * Helper function for loading component data in series
 * @param {[]} dataArray Array of inputs per data requirement
 * @param {string} setLoadingMessage The loading message
 * @return {promise} A promise chain for executing data fetch in series
 */
export const loadDataSerialSimple = (
  dataArray: Array<{
    checkRequiredData?: () => mixed,
    action: () => Dispatch<*>,
    loadingMessage?: string,
    signature?: string // give the action an ID to trace data flow
  }>,
  setLoadingMessage?: string => void = () => {}
) => {
  const promiseArray = dataArray.map(data => () => {
    if (!data.checkRequiredData) {
      if (data.loadingMessage) setLoadingMessage(data.loadingMessage)
      return data.action()
    } else if (!data.checkRequiredData()) {
      if (data.loadingMessage) setLoadingMessage(data.loadingMessage)

      /* \$FlowFixMe This comment suppresses an error found when upgrading
       * Flow to v0.99.0. To view the error, delete this comment and run Flow.
       */
      return data.action().then(res => {
        // $FlowFixMe
        if ((res && res.error) || !data.checkRequiredData()) {
          return Promise.reject({ ...res, signature: data.signature })
        } else {
          return Promise.resolve(res)
        }
      })
    } else {
      return Promise.resolve(data)
    }
  })

  return (
    promiseArray
      /* \$FlowFixMe This comment suppresses an error found when upgrading Flow
       * to v0.98.0. To view the error, delete this comment and run Flow. */
      .reduce((promise, nextAction: () => Promise<any>) => {
        return promise.then(() => nextAction())
      }, Promise.resolve())

      .catch(err => {
        if (err && err.payload && err.payload.statusCode === 503) {
          setLoadingMessage('Service unavailable, please refresh to try again')
          return Promise.reject(err)
        } else {
          setLoadingMessage('Error loading data')
          const ERROR_TEXT =
            err && err.error
              ? 'NETWORK_ERROR'
              : withSignature(
                  'FETCH_WENT_THROUGH_BUT_DATA_REQUIREMENT_NOT_FULFILLED',
                  err.signature
                )
          logger.error(new Error(ERROR_TEXT), { err })
          return Promise.reject(err)
        }
      })
  )
}

/**
 * Helper function for loading component data in parallel
 * @param {[]} dataArray Array of inputs per data requirement
 * @param {func} setLoadingMessage Function for setting loading message
 * @param {string} loadingMessage The loading message
 * @return {promise} A promise chain for executing data fetch in parallel
 */
export const loadDataParallelSimple = (
  dataArray: Array<{
    checkRequiredData?: () => mixed,
    action: () => Dispatch<*>,
    signature?: string // give the action an ID to trace data flow
  }>,
  setLoadingMessage?: string => void,
  loadingMessage?: string
) => {
  if (loadingMessage && setLoadingMessage) setLoadingMessage(loadingMessage)

  const promiseArray = dataArray.map(data => {
    if (!data.checkRequiredData) return data.action()

    if (!data.checkRequiredData()) {
      const prom = data.action()

      // Check that the action is a promise
      try {
        /* \$FlowFixMe This comment suppresses an error found when upgrading
         * Flow to v0.99.0. To view the error, delete this comment and run
         * Flow. */
        prom.then
      } catch (e) {
        const err = new Error(
          withSignature(
            'IN loadDataParallelSimple, MISSING thenable action',
            data.signature
          )
        )
        logger.error(err, {
          dataArray,
          data
        })
        return Promise.reject(err)
      }

      /* \$FlowFixMe This comment suppresses an error found when upgrading
       * Flow to v0.99.0. To view the error, delete this comment and run Flow.
       */
      return prom.then(res => {
        // $FlowFixMe
        if ((res && res.error) || !data.checkRequiredData()) {
          return Promise.reject({ ...res, signature: data.signature })
        } else {
          return Promise.resolve(res)
        }
      })
    } else {
      return Promise.resolve(data)
    }
  })

  return Promise.all(promiseArray)
    .then(values => {
      return Promise.resolve(values)
    })
    .catch(err => {
      if (err && err.payload && err.payload.statusCode === 503) {
        if (setLoadingMessage)
          setLoadingMessage('Service unavailable, please refresh to try again')
        return Promise.reject(err)
      } else {
        if (setLoadingMessage) setLoadingMessage('Error loading data')
        logger.error({
          error: err,
          dataArray: dataArray.map(d => ({
            action: d.action ? d.action.toString() : null,
            checkRequiredData: d.checkRequiredData
              ? d.checkRequiredData.toString()
              : null
          })),
          reason:
            err && err.error
              ? 'Network error'
              : withSignature(
                  'FETCH_WENT_THROUGH_BUT_DATA_REQUIREMENT_NOT_FULFILLED',
                  err.signature
                )
        })
        return Promise.reject(err)
      }
    })
}
